import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import $api from "@/zapi-admin/shared/api/axios";
import { AppLoader } from "@/zapi-admin/shared/components/AppLoader";
import { AuthLogsResponse } from "../model/types";
import { AuthLogsTable } from "./AuthLogsTable";
import { UserSelect } from "@/zapi-admin/pages/AiLogsPage/ui/UserSelect";
import { AppButton } from "@libs/zapi-ui/custom";
import { RefreshCw } from "lucide-react";
import { EventSelect } from "./EventSelect";
import { SourceSelect } from "./SourceSelect";

export function AuthLogsView() {
  const [user, setUser] = useState(() => localStorage.getItem("authLogsUser") || "");
  const [event, setEvent] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const [isRefetching, setIsRefetching] = useState(false);
  const pageSize = 10;

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["auth-logs", page, user, event, source],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });
      if (user) params.append("userId", user);
      if (event) params.append("event", event);
      if (source) params.append("source", source);

      const response = await $api.get(`/admin/auth-logs?${params.toString()}`);
      return {
        data: AuthLogsResponse.parse(response.data.data),
        total: response.data.total as number
      };
    }
  });

  const handleRefetch = async () => {
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  };

  return (
    <div data-testid="auth-logs-view">
      <div className="flex gap-4 mb-6" data-testid="auth-logs-filters">
        <UserSelect
          userId={user}
          setUserId={(v) => {
            setUser(v);
            localStorage.setItem("authLogsUser", v);
            handlePageChange(1);
          }}
        />
        <EventSelect
          event={event}
          setEvent={(v) => {
            setEvent(v);
            handlePageChange(1);
          }}
        />
        <SourceSelect
          source={source}
          setSource={(v) => {
            setSource(v);
            handlePageChange(1);
          }}
        />
        <div className="flex items-center gap-2 ml-auto">
          <AppButton
            appearance="outlined"
            color="zinc950"
            onClick={handleRefetch}
            disabled={isLoading || isRefetching}
            className="flex items-center gap-2 px-4 py-2 h-fit"
          >
            <RefreshCw size={16} className={isRefetching ? "animate-spin" : ""} />
            {isRefetching ? "Refreshing..." : "Refresh"}
          </AppButton>
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading && (
          <div className="flex items-center justify-center">
            <AppLoader />
          </div>
        )}

        {!isLoading && (
          <AuthLogsTable
            logs={data?.data || []}
            pageSize={pageSize}
            page={page}
            total={data?.total}
            setPage={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
